import {css, StyleSheet} from 'aphrodite';
import reactUpdate from 'immutability-helper';
import React from 'react';
import _ from 'underscore';

import GroupSelector from 'parties/GroupSelector';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import AddIcon from 'ui-library/icons/AddIcon';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import {capitalize, typeToPluralized} from 'util/NameUtil';

interface Group {
  id: string;
  name: string;
}

export interface GroupRowInfo<T extends Group> {
  role: number;
  group: T | null;
}

interface GroupRowProps<T extends Group> {
  onRemove: () => void;
  onUpdate: (groupRowInfo: GroupRowInfo<T>) => void;
  disabled: boolean;
  groups: Array<T>;
  groupRowInfo: GroupRowInfo<T>;
  groupType: string;
  roleToText: {[role: number]: string};
}

export class GroupRow<T extends Group> extends React.Component<GroupRowProps<T>> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateRole = (role: number) => {
    this.props.onUpdate({
      ...this.props.groupRowInfo,
      role,
    });
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleSelectGroup = (group: T) => {
    this.props.onUpdate({
      ...this.props.groupRowInfo,
      group,
    });
  };

  render(): JSX.Element {
    const selectedGroup = this.props.groupRowInfo.group;
    return (
      <div className={css(styles.groupRow)}>
        <GroupSelector
          className={css(styles.groupRowField)}
          disabled={this.props.disabled}
          groups={this.props.groups}
          groupType={this.props.groupType}
          onSelect={this._handleSelectGroup}
          selectedGroup={selectedGroup}
        />
        <SelectDropdown
          className={css(styles.groupRowField)}
          menu={
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.FULL} value={this.props.groupRowInfo.role}>
              {_.sortBy(Object.keys(this.props.roleToText), (role) => -role).map((role) => {
                // Object.keys transforms keys into strings, so transform back to match Role enums.
                const roleNum = Number(role);
                return (
                  <Option key={role} value={roleNum} onSelect={this._handleUpdateRole}>
                    {this.props.roleToText[roleNum]}
                  </Option>
                );
              })}
            </Menu>
          }
          disabled={this.props.disabled}
          displayName={this.props.roleToText[this.props.groupRowInfo.role]}
        />
        <div className={css(styles.groupRowRemoveIcon)}>
          {!this.props.disabled && (
            <BenchlingIcon icon="close" className="u-faded u-pointer" onClick={this.props.onRemove} />
          )}
        </div>
      </div>
    );
  }
}

interface GroupSectionProps<T extends Group> {
  disabled: boolean;
  onUpdate: (groupRowInfos: Array<GroupRowInfo<T>>) => void;
  groups: Array<T>;
  groupRowInfos: Array<GroupRowInfo<T>>;
  // Used for displaying text about the group type.
  groupType: string;
  emptyRowInfo: GroupRowInfo<T>;
  roleToText: {[role: number]: string};
}

/**
 * Section to help add groups (teams, organizations) mapped to some role.
 */
export default class GroupsSection<T extends Group> extends React.Component<GroupSectionProps<T>> {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleAddGroup = () => {
    this.props.onUpdate([...this.props.groupRowInfos, this.props.emptyRowInfo]);
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleRemoveGroup = (index: number) => {
    // Reset to template if only one group row.
    if (index === 0 && this.props.groupRowInfos.length === 1) {
      this.props.onUpdate(reactUpdate(this.props.groupRowInfos, {[index]: {$set: this.props.emptyRowInfo}}));
    } else {
      this.props.onUpdate(reactUpdate(this.props.groupRowInfos, {$splice: [[index, 1]]}));
    }
  };

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  _handleUpdateGroupRow = (index: number, groupRowInfo: GroupRowInfo<T>) => {
    this.props.onUpdate(reactUpdate(this.props.groupRowInfos, {[index]: {$set: groupRowInfo}}));
  };

  render(): JSX.Element {
    const addedGroupIds = this.props.groupRowInfos
      .filter((groupRowInfo) => groupRowInfo.group != null)
      .map((groupRowInfo) => groupRowInfo.group!.id);
    return (
      <div className="u-spacing-top">
        <div>
          <FieldLabel label={capitalize(typeToPluralized(this.props.groupType))} />
          {!this.props.disabled && this.props.groups.length > addedGroupIds.length && (
            <AddIcon className="u-mini-spacing-left" onClick={this._handleAddGroup} />
          )}
        </div>
        {this.props.groupRowInfos.map((groupRowInfo, i) => (
          <GroupRow
            key={i}
            disabled={this.props.disabled}
            groups={this.props.groups.filter((group) => !addedGroupIds.includes(group.id))}
            groupRowInfo={groupRowInfo}
            onUpdate={(updatedGroupRowInfo) => {
              this._handleUpdateGroupRow(i, updatedGroupRowInfo);
            }}
            onRemove={() => {
              this._handleRemoveGroup(i);
            }}
            roleToText={this.props.roleToText}
            groupType={this.props.groupType}
          />
        ))}
      </div>
    );
  }
}

const styles = StyleSheet.create({
  groupRow: {
    alignItems: 'center',
    display: 'flex',
    marginBottom: 12,
  },
  groupRowField: {
    flex: 1,
    marginRight: 12,
  },
  groupRowRemoveIcon: {
    flex: 'none',
  },
});
